import { Card } from '@chakra-ui/react';
import {
  IPaymentGateway,
  PaymentGatewayStatusDisplayText,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { StripeIcon } from '@monetize/ui/icons';
import { useEffect, useState } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useDeletePaymentGatewayByPaymentGatewayId,
  useGetPaymentGatewaysByTenant,
  useSetDefaultPaymentGateway,
} from '../../../../api/paymentsService';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MText,
} from '../../../../components/Monetize';
import { ButtonCard } from '../../../../components/Monetize/Cards/ButtonCard';
import { MDataTableActions } from '../../../../components/Monetize/DataTable';
import { EntityJsonViewerIcon } from '../../../../components/Monetize/EntityJsonViewerIcon';
import { MEntityStatusBadge } from '../../../../components/Monetize/MEntityStatusBadge';
import { PaymentMethodTypeEnumDisplay } from '../../../../constants/paymentMethods';
import { useQuoteSettings } from '../../../../hooks';
import { useDocumentHead } from '../../../../services/documentHead';
import StripeForm from './components/StripeForm';

const PAYMENT_GATEWAY_LOCKED_MESSAGE =
  'Disable “Collect payment before Quote processing” under Quote Settings before removing this Payment Gateway.';

const PaymentProcessor = () => {
  const [formVisible, setFormVisible] = useState<boolean>(false);
  const [gatewayId, setGatewayID] = useState<string>('');

  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Settings', 'Payment Gateway');
  }, []);

  const { isLoading, data } = useGetPaymentGatewaysByTenant({
    meta: { showErrorToast: true },
  });
  const { quoteSettings } = useQuoteSettings();
  const collectPaymentBeforeQuoteProcessing =
    !!quoteSettings?.collectPaymentBeforeQuoteProcessing;
  const { mutateAsync: deletePaymentGateway } =
    useDeletePaymentGatewayByPaymentGatewayId({
      onSuccess: () => {
        setGatewayID('');
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const doSetDefaultGateway = useSetDefaultPaymentGateway({
    onSuccess: () => {
      resetFormVisible();
    },
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });
  const setDefaultPaymentGateway = (gateway: IPaymentGateway) => {
    doSetDefaultGateway.mutate({
      gatewayId: gateway.id!,
    });
  };

  const gateways = (data?.content || []) as IPaymentGateway[];

  const handleFormVisible = () => {
    setGatewayID('');
    setFormVisible(true);
  };

  const resetFormVisible = () => {
    setFormVisible(false);
  };

  const handleOpenEdit = (id: string) => {
    setGatewayID(id);
    setFormVisible(true);
  };

  const actionMenu = (rowData: IPaymentGateway) => {
    const actions = [
      {
        title: 'Set as Default',
        enabled: !rowData.defaultGateway,
        action: () => setDefaultPaymentGateway(rowData),
      },
      {
        title: 'Delete',
        color: 'danger',
        enabled: !rowData.locked,
        isDisabled: collectPaymentBeforeQuoteProcessing,
        tooltipMsg: collectPaymentBeforeQuoteProcessing
          ? PAYMENT_GATEWAY_LOCKED_MESSAGE
          : undefined,
        action: () => deletePaymentGateway(rowData),
      },
    ];

    return <MDataTableActions actions={actions} />;
  };

  const getAllowedPaymentMethodsText = (gateway: IPaymentGateway) => {
    const sortOrder: Partial<Record<PaymentMethodTypeEnum, number>> = {
      [PaymentMethodTypeEnum.CREDIT_CARD]: 0,
      [PaymentMethodTypeEnum.DIRECT_DEBIT]: 1,
      [PaymentMethodTypeEnum.ACH_CREDIT]: 2,
      [PaymentMethodTypeEnum.US_BANK_TRANSFER]: 3,
    };

    return gateway.allowedPaymentMethods
      .filter(({ enabled }) => enabled)
      .sort((a, b) => {
        const orderA = sortOrder[a.method] ?? 1000;
        const orderB = sortOrder[b.method] ?? 1000;
        return orderA - orderB;
      })
      .map(({ method }) => PaymentMethodTypeEnumDisplay[method])
      .join(', ');
  };

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Payment Gateways" />

      <MBox maxW="600px">
        <MBox mb="4">
          <MText color="tGray.darkPurple">
            Add Payment Gateways here to allow processing of electronic payment
            methods like credit cards and ACH. If you do not see your specific
            gateway here, please contact support to get it added.
          </MText>
        </MBox>
        {isLoading ? (
          <MSkeleton height={24} w="full" />
        ) : (
          <MBox>
            {gateways?.length === 0 && !formVisible && (
              <ButtonCard
                h="82px"
                titleLeftContent={<StripeIcon />}
                title={'Stripe'}
                description={`Connect to Stripe`}
                isDisabled={false}
                titleRightContent={undefined}
                onClick={handleFormVisible}
              />
            )}
            {gateways?.length > 0 && !formVisible && (
              <MBox>
                {gateways?.map((item) => (
                  <Card.Root
                    key={item.id}
                    variant="edit"
                    width="37.5rem"
                    mt="2"
                  >
                    <Card.Header>
                      <MFlex justify="space-between" align="center">
                        <MFlex alignItems="center">
                          <StripeIcon />
                          <MText color="tPurple.dark" fontSize="sm" ml="2">
                            Stripe
                          </MText>
                          {item.status && (
                            <MEntityStatusBadge
                              status={
                                PaymentGatewayStatusDisplayText[item.status]
                              }
                            />
                          )}
                          <EntityJsonViewerIcon json={item} />
                        </MFlex>
                        <MFlex align="center">
                          <MBox>
                            <MButton
                              variant="tertiary"
                              size="sm"
                              color="tIndigo.base"
                              onClick={() => handleOpenEdit(item.id)}
                            >
                              Edit
                            </MButton>
                          </MBox>
                          {actionMenu(item)}
                        </MFlex>
                      </MFlex>
                    </Card.Header>
                    <Card.Body>
                      <MFlex gap={8}>
                        <MFlex direction="column">
                          <MText fontWeight="bold" color="tPurple.dark">
                            Name
                          </MText>
                          <MText>{item.description}</MText>
                        </MFlex>
                        <MFlex direction="column">
                          <MText fontWeight="bold" color="tPurple.dark">
                            Payment Method Support
                          </MText>
                          <MText>{getAllowedPaymentMethodsText(item)}</MText>
                        </MFlex>
                      </MFlex>
                    </Card.Body>
                  </Card.Root>
                ))}
              </MBox>
            )}
            {formVisible && (
              <StripeForm
                gatewayId={gatewayId}
                resetFormVisible={resetFormVisible}
              />
            )}
          </MBox>
        )}
      </MBox>
    </MPageContainer>
  );
};

export default PaymentProcessor;
